/**
 * Web implementation of fleetAPI.
 *
 * - Real-time updates: browser WebSocket → /ws (server proxies to AWS API Gateway WS)
 * - Commands: POST /api/fleet/commands
 * - Log operations: GET /api/proxy/events/logs/... (via generic proxy)
 * - Downloads: server streams file with Content-Disposition: attachment
 */

// ---------------------------------------------------------------------------
// WebSocket
// ---------------------------------------------------------------------------

type WsCallback<T> = (msg: T) => void;

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const deviceStatusListeners: WsCallback<unknown>[] = [];
const commandListeners: WsCallback<unknown>[] = [];
const wsReconnectListeners: WsCallback<void>[] = [];
const wsConnectionStateListeners: WsCallback<{ connected: boolean }>[] = [];
let wsHasConnectedOnce = false;

function emitWsConnectionState(connected: boolean) {
  for (const cb of wsConnectionStateListeners) cb({ connected });
}

function wsConnect() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  ws = new WebSocket(`${proto}//${location.host}/ws`);

  ws.onopen = () => {
    if (wsHasConnectedOnce) {
      for (const cb of wsReconnectListeners) cb();
    }
    wsHasConnectedOnce = true;
    emitWsConnectionState(true);
  };

  ws.onmessage = (ev) => {
    try {
      const parsed = JSON.parse(ev.data as string);
      const items: { type?: string }[] = Array.isArray(parsed) ? parsed : [parsed];
      for (const msg of items) {
        if (msg.type === 'device_status_update') {
          for (const cb of deviceStatusListeners) cb(msg);
        } else if (msg.type === 'command_update') {
          for (const cb of commandListeners) cb(msg);
        }
      }
    } catch { /* ignore non-JSON */ }
  };

  ws.onclose = () => {
    emitWsConnectionState(false);
    if (deviceStatusListeners.length || commandListeners.length) {
      reconnectTimer = setTimeout(wsConnect, 3000);
    }
  };

  ws.onerror = () => ws?.close();
}

function wsForceReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
    ws = null;
  }
  wsConnect();
}

// ---------------------------------------------------------------------------

type ProgressEvent = {
  completed: number;
  total: number;
  key: string;
  status: 'success' | 'failed';
  error?: string;
};
const progressListeners: WsCallback<ProgressEvent>[] = [];

function emitProgress(ev: ProgressEvent) {
  for (const cb of progressListeners) cb(ev);
}

// ---------------------------------------------------------------------------
// File registry (for openFileDialog — maps fake path → File)
// ---------------------------------------------------------------------------

const fileRegistry = new Map<string, File>();

function triggerBrowserDownload(url: string, filename?: string) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BASE_PROXY = '/api/proxy/events';

async function eventsGet(
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<unknown> {
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v!))}`)
        .join('&')
    : '';
  const url = `${BASE_PROXY}${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  const text = await res.text();
  let data: unknown;
  try { data = JSON.parse(text); } catch { data = text; }
  return { ok: res.status >= 200 && res.status < 300, status: res.status, data };
}

async function eventsDelete(
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<unknown> {
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v!))}`)
        .join('&')
    : '';
  const url = `${BASE_PROXY}${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
  const text = await res.text();
  let data: unknown;
  try { data = JSON.parse(text); } catch { data = text; }
  return { ok: res.status >= 200 && res.status < 300, status: res.status, data };
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export function createWebFleetApi() {
  return {
    // Real-time subscriptions
    onDeviceStatusUpdate(callback: WsCallback<unknown>): () => void {
      deviceStatusListeners.push(callback);
      wsConnect();
      return () => {
        const i = deviceStatusListeners.indexOf(callback);
        if (i !== -1) deviceStatusListeners.splice(i, 1);
      };
    },

    onCommandUpdate(callback: WsCallback<unknown>): () => void {
      commandListeners.push(callback);
      wsConnect();
      return () => {
        const i = commandListeners.indexOf(callback);
        if (i !== -1) commandListeners.splice(i, 1);
      };
    },

    onWsReconnected(callback: WsCallback<void>): () => void {
      wsReconnectListeners.push(callback);
      return () => {
        const i = wsReconnectListeners.indexOf(callback);
        if (i !== -1) wsReconnectListeners.splice(i, 1);
      };
    },

    onWsConnectionState(callback: WsCallback<{ connected: boolean }>): () => void {
      wsConnectionStateListeners.push(callback);
      return () => {
        const i = wsConnectionStateListeners.indexOf(callback);
        if (i !== -1) wsConnectionStateListeners.splice(i, 1);
      };
    },

    reconnectWebSocket(): Promise<{ ok: boolean; error?: string }> {
      wsForceReconnect();
      return Promise.resolve({ ok: true });
    },

    // Commands
    async submitCommand(params: unknown): Promise<unknown> {
      const res = await fetch('/api/fleet/commands', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const data = await res.json().catch(() => null);
      const error = res.ok ? undefined
        : (data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : `HTTP ${res.status}`);
      return { ok: res.ok, status: res.status, data, error };
    },

    // File picker (used by FirmwareUploadView)
    openFileDialog(): Promise<{ canceled: boolean; filePaths: string[] }> {
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.bin,.hex';
        input.style.display = 'none';
        document.body.appendChild(input);
        input.onchange = () => {
          document.body.removeChild(input);
          const file = input.files?.[0];
          if (!file) { resolve({ canceled: true, filePaths: [] }); return; }
          const key = `web-file://${Date.now()}/${file.name}`;
          fileRegistry.set(key, file);
          resolve({ canceled: false, filePaths: [key] });
        };
        input.addEventListener('cancel', () => {
          document.body.removeChild(input);
          resolve({ canceled: true, filePaths: [] });
        });
        input.click();
      });
    },

    // Log listing — proxy to Events API
    listLogDevices(args?: { eventId?: string }): Promise<string[]> {
      const path = args?.eventId
        ? `/events/${encodeURIComponent(args.eventId)}/logs/devices`
        : '/logs/devices';
      return eventsGet(path).then((res) => {
        const d = (res as { data?: { deviceIds?: string[] } }).data;
        return d?.deviceIds ?? [];
      });
    },

    listLogFolders(args?: { logType?: string; eventId?: string; raceId?: string }): Promise<string[]> {
      const path = args?.eventId
        ? `/events/${encodeURIComponent(args.eventId)}/logs/folders`
        : '/logs/folders';
      return eventsGet(path, { logType: args?.logType, raceId: args?.raceId }).then((res) => {
        const d = (res as { data?: { folders?: string[] } }).data;
        return d?.folders ?? [];
      });
    },

    listLogFiles(args: {
      deviceId: string;
      logType?: string;
      logFolder?: string;
      eventId?: string;
    }): Promise<unknown[]> {
      const path = args.eventId
        ? `/events/${encodeURIComponent(args.eventId)}/logs/files`
        : '/logs/files';
      return eventsGet(path, {
        deviceId: args.deviceId,
        logType: args.logType,
        logFolder: args.logFolder,
      }).then((res) => {
        const d = (res as { data?: { objects?: unknown[] } }).data;
        return d?.objects ?? [];
      });
    },

    listLogFolderFiles(args: {
      logType: string;
      logFolder?: string;
      eventId?: string;
      raceId?: string;
    }): Promise<unknown[]> {
      const path = args.eventId
        ? `/events/${encodeURIComponent(args.eventId)}/logs/folders/files`
        : '/logs/folders/files';
      return eventsGet(path, {
        logType: args.logType,
        logFolder: args.logFolder,
        raceId: args.raceId,
      }).then((res) => {
        const d = (res as { data?: { objects?: unknown[] } }).data;
        return d?.objects ?? [];
      });
    },

    listEventIndexedLogFiles(args: {
      eventId: string;
      logType: string;
      raceId?: string;
    }): Promise<unknown[]> {
      return eventsGet(
        `/events/${encodeURIComponent(args.eventId)}/logs/index/files`,
        { logType: args.logType, raceId: args.raceId },
      ).then((res) => {
        const d = (res as { data?: { objects?: unknown[] } }).data;
        return d?.objects ?? [];
      });
    },

    getLogPreview(args: {
      key: string;
      mode?: 'head' | 'tail';
      maxBytes?: number;
      eventId?: string;
    }): Promise<string> {
      const path = args.eventId
        ? `/events/${encodeURIComponent(args.eventId)}/logs/preview`
        : '/logs/preview';
      return eventsGet(path, {
        key: args.key,
        mode: args.mode,
        maxBytes: args.maxBytes,
      }).then((res) => {
        const d = (res as { data?: { text?: string } }).data;
        return d?.text ?? '';
      });
    },

    // Admin-only: the backend rejects this for non-admins; no event-scoped variant exists.
    async deleteLogFile(args: { key: string }): Promise<{ ok: boolean; error?: string }> {
      const res = (await eventsDelete('/logs/file', { key: args.key })) as {
        ok: boolean;
        data?: { error?: string };
      };
      if (!res.ok) {
        return { ok: false, error: res.data?.error || 'Failed to delete log file' };
      }
      return { ok: true };
    },

    // Download: triggers a browser file download via the server proxy
    async downloadLogFile(args: {
      key: string;
      suggestedName?: string;
      eventId?: string;
    }): Promise<{ ok: boolean; canceled?: boolean; filePath?: string; error?: string }> {
      const params = new URLSearchParams({ key: args.key });
      if (args.eventId) params.set('eventId', args.eventId);
      triggerBrowserDownload(`/api/fleet/logs/download?${params}`, args.suggestedName);
      return { ok: true };
    },

    // Bulk download: gets presigned URLs then triggers each download
    async bulkDownloadLatestLogs(args: {
      deviceIds: string[];
      logType: 'race' | 'debug';
      folderName?: string;
      sourceMode?: string;
      sourceLogFolder?: string;
    }): Promise<{ ok: boolean; downloaded?: unknown[]; missing?: string[]; failed?: unknown[] }> {
      const res = await fetch('/api/fleet/logs/bulk-download', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...args, mode: 'latest' }),
      });
      const data = await res.json().catch(() => ({ ok: false })) as { ok: boolean; results?: Array<{ key: string; url?: string; error?: string }> };

      const total = data.results?.length ?? 0;
      let completed = 0;

      for (const item of data.results ?? []) {
        if (item.url) {
          triggerBrowserDownload(item.url, item.key.split('/').pop());
          emitProgress({ completed: ++completed, total, key: item.key, status: 'success' });
        } else {
          emitProgress({ completed: ++completed, total, key: item.key, status: 'failed', error: item.error });
        }
      }

      return {
        ok: true,
        downloaded: data.results?.filter((r) => r.url) ?? [],
        missing: [],
        failed: data.results?.filter((r) => !r.url) ?? [],
      };
    },

    async bulkDownloadSelectedLogs(args: {
      keys: string[];
      folderName?: string;
      eventId?: string;
    }): Promise<{ ok: boolean; downloaded?: unknown[]; failed?: unknown[] }> {
      const res = await fetch('/api/fleet/logs/bulk-download', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys: args.keys, eventId: args.eventId }),
      });
      const data = await res.json().catch(() => ({ ok: false })) as { ok: boolean; results?: Array<{ key: string; url?: string; error?: string }> };

      const total = data.results?.length ?? 0;
      let completed = 0;

      for (const item of data.results ?? []) {
        if (item.url) {
          triggerBrowserDownload(item.url, item.key.split('/').pop());
          emitProgress({ completed: ++completed, total, key: item.key, status: 'success' });
        } else {
          emitProgress({ completed: ++completed, total, key: item.key, status: 'failed', error: item.error });
        }
      }

      return {
        ok: true,
        downloaded: data.results?.filter((r) => r.url) ?? [],
        failed: data.results?.filter((r) => !r.url) ?? [],
      };
    },

    onBulkDownloadProgress(callback: WsCallback<ProgressEvent>): () => void {
      progressListeners.push(callback);
      return () => {
        const i = progressListeners.indexOf(callback);
        if (i !== -1) progressListeners.splice(i, 1);
      };
    },
  };
}

/** Called by FirmwareUploadView to get the File object for a web-file:// path */
export function getWebFile(path: string): File | undefined {
  return fileRegistry.get(path);
}
